import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import CloseButton from '../../common/CloseButton/CloseButton';
import InputBoxSmall from '../../common/InputBox/InputBoxSmall';
import '../../common/CommonText.css';
import { isRequired, isTextOnly } from '../../../validation';
import { showAlert } from '../../common/ExportLabel/Exporterror';

const EditInternalUserRolesPopup = ({ 
  isOpen, 
  title, 
  fields, 
  onSave, 
  onClose, 
  tableHeaders, 
  initialTableRows, 
  popupErrorMessage, 
  selected, 
}) => { 
  const [tableRows, setTableRows] = useState(initialTableRows);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    setTableRows(initialTableRows);
  }, [initialTableRows]);

  useEffect(() => {
    if (isOpen && selected) {
      // Initialize formData with selected item's data
      const initialFormData = {
        name: selected.name || '',
        permissions: selected.permissions || []
      };
      setFormData(initialFormData);
    }
  }, [isOpen, selected]);

  const handleInputChange = (field, value) => {
    setFormData((prevValues) => ({
      ...prevValues,
      [field]: value,
    }));
  };

  const handleCheckboxToggle = (permissionId) => {
    setFormData((prevValues) => {
      const currentPermissions = prevValues.permissions || [];
      const exists = currentPermissions.some((perm) => perm.id === permissionId);
      
      const updatedPermissions = exists 
        ? currentPermissions.filter((perm) => perm.id !== permissionId)
        : [...currentPermissions, { id: permissionId }];
      
      return {
        ...prevValues,
        permissions: updatedPermissions,
      };
    });
  };

  const validateField = (field) => {
    const validators = field.validators || [];
    const value = formData[field.name] || "";
    
    for (const validator of validators) {
      const result = validator(value);
      if (typeof result === "string") {
        return result;
      }
    }
    return null;
  };

  const handleSubmit = () => {
    const errors = {};
    let hasErrors = false;
    
    fields.forEach(field => {
      const error = validateField(field);
      if (error) {
        errors[field.name] = error;
        hasErrors = true;
      }
    });

    if ((formData.permissions || []).length === 0) {
      showAlert("Please select at least one permission.");
      return;
    }

    setFormErrors(errors);
    if (hasErrors) return;

    const finalData = {
      id: selected?.id,
      ...formData,
      permissions: (formData.permissions || []).map((perm) => perm.id),
    };
    
    onSave(finalData);
  };

  const handleClose = () => {
    setFormErrors({});
    setFormData({});
    if (onClose) onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="popup-overlay">
      <div className="popup popup1 internal-user-popup">
        <CloseButton onClick={handleClose} />
        
        {popupErrorMessage && (
          <div className="popup-error-message fts-13" style={{ color: 'red', marginBottom: '10px' }}>
            {popupErrorMessage}
          </div>
        )}
        
        <div className="popup-name-button">
          <h2 className="popup-name">{title}</h2>
          <button className="popup-add-button white fts-15" onClick={handleSubmit}>
            Save
          </button>
        </div>
        
        <div className="add-input-box-container edit-popup">
          {fields.map((field, index) => {
            switch (field.type) {
              case 'small-input':
                return (
                  <div key={index} className="field-container w-50">
                    <InputBoxSmall 
                      key={index} 
                      {...field} 
                      value={formData[field.name] || ''}
                      onChange={(e) => handleInputChange(field.name, e.target.value)}
                      validators={isRequired}
                      error={formErrors[field.name]}
                    />
                  </div>
                );
              default:
                return null;
            }
          })}
        </div>
        
        {tableHeaders && tableHeaders.length > 0 && tableRows.length > 0 && (
          <div className="internal-user-table-container">
            <div className="internal-user-table-scrollable">
              <table className="internal-user-table">
                <thead className="internal-user-table-thead common-table-head">
                  <tr className="internal-user-role-tr common-table-thead-tr">
                    <th className="text-left">Permission</th>
                    <th className="text-center">View</th>
                    <th className="text-center">View All</th>
                    <th className="text-center">Add</th>
                    <th className="text-center">Edit</th>
                    <th className="text-center">Archive</th>
                    <th className="text-center">Delete</th>
                  </tr>
                </thead>
                <tbody className="internal-user-table-tbody">
                  {tableRows.map((row, rowIndex) => (
                    <tr key={rowIndex} className="internal-user-table-tbody-tr">
                      <td>
                        {row.feature_name
                          .replace(/_/g, ' ')
                          .split(' ')
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(' ')}
                      </td>
                      {["view","view_all","add", "edit", "archive", "delete"].map((accessName, colIndex) => {
                        const permission = row.permissions.find(
                          (perm) => perm.access_name === accessName
                        );
                        return (
                          <td key={colIndex} className="text-center ">
                            {permission ? (
                              <input 
                                className="table-selectbox internal-user-table-checkbox "
                                type="checkbox"
                                checked={
                                  (formData.permissions || []).some(
                                    (perm) => perm.id === permission.id
                                  ) || false
                                }
                                onChange={() => handleCheckboxToggle(permission.id)}
                              />
                            ) : (
                              <span>&nbsp;</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


EditInternalUserRolesPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  fields: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  tableHeaders: PropTypes.arrayOf(PropTypes.string),
  initialTableRows: PropTypes.arrayOf(PropTypes.array),
  selected: PropTypes.object,
};

export default EditInternalUserRolesPopup;
